'use client'

import { CheckCircle2Icon, CircleHelpIcon, XCircleIcon } from 'lucide-react'
import { memo, useEffect, useState } from 'react'

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { getExchangeLabel } from '@/lib/configs/exchange-registry'
import { cn } from '@/lib/utils'
import { isAlwaysOpen, useVenueSchedule, useVenueStatus } from '@/service/venue-status.hook'
import { Exchange } from '@/types/generated/common/types'
import { ExchangeStatusType, VenueSchedule, WeeklySession } from '@/types/generated/public_api'

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] // 0 = Monday … 6 = Sunday

export function exchangeDisplayName(exchange: Exchange): string {
  return getExchangeLabel(exchange)
}

export function formatDuration(ms: number): string {
  const totalMinutes = Math.max(Math.floor(ms / 60_000), 0)
  const days = Math.floor(totalMinutes / (60 * 24))
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60)
  const minutes = totalMinutes % 60
  if (days > 0) return `${days}d ${hours}h`
  if (hours > 0) return `${hours}h ${minutes}m`
  return `${minutes}m`
}

/** Collapses consecutive weekdays sharing the same window into "Mon–Fri 09:30–16:00" rows. */
function compressWeeklySessions(sessions: WeeklySession[]): string[] {
  const sorted = [...sessions].sort((a, b) => a.weekday - b.weekday)
  const rows: string[] = []
  let runStart: WeeklySession | null = null
  let runEnd: WeeklySession | null = null

  const flush = () => {
    if (!runStart || !runEnd) return
    const dayLabel =
      runStart.weekday === runEnd.weekday
        ? WEEKDAY_LABELS[runStart.weekday]
        : `${WEEKDAY_LABELS[runStart.weekday]}–${WEEKDAY_LABELS[runEnd.weekday]}`
    rows.push(`${dayLabel} ${runStart.open}–${runStart.close}`)
  }

  for (const session of sorted) {
    if (
      runStart &&
      runEnd &&
      session.weekday === runEnd.weekday + 1 &&
      session.open === runStart.open &&
      session.close === runStart.close
    ) {
      runEnd = session
      continue
    }
    flush()
    runStart = session
    runEnd = session
  }
  flush()
  return rows
}

export function RegularHours({ schedule }: { schedule: VenueSchedule }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs font-medium text-white/50">Regular hours</span>
      {schedule.alwaysOpen ? (
        <span className="text-xs">24/7</span>
      ) : (
        <>
          {compressWeeklySessions(schedule.weeklySessions).map((row) => (
            <span key={row} className="text-xs tabular-nums">
              {row}
            </span>
          ))}
          <span className="text-xs text-white/40">{schedule.timezone} local time</span>
        </>
      )}
    </div>
  )
}

interface MarketStatusBadgeProps {
  exchange: Exchange | undefined
  className?: string
  tooltipContentClassName?: string
}

/**
 * Live OPEN/CLOSED badge for a venue, with the weekly "Regular Hours"
 * tooltip. The badge is driven by `GetVenueStatus` (holiday/DST/overnight
 * aware); the tooltip hours are the static `GetVenueSchedules` hint. Errors
 * render a neutral "unavailable" state — never CLOSED.
 *
 * Memoized (primitive props only): the account selector renders one of these
 * per account row plus the trigger, so parent re-renders during account
 * switching shouldn't cascade into every Radix Tooltip subtree.
 */
export const MarketStatusBadge = memo(function MarketStatusBadge({
  exchange,
  className,
  tooltipContentClassName,
}: MarketStatusBadgeProps) {
  const { data: status, isError } = useVenueStatus(exchange)
  const schedule = useVenueSchedule(exchange)

  // Tick once a minute so the "opens/closes in" countdown stays current.
  const [now, setNow] = useState(() => Date.now())
  const hasBoundary = status?.nextChangeMs !== undefined
  useEffect(() => {
    if (!hasBoundary) return
    const interval = window.setInterval(() => setNow(Date.now()), 60_000)
    return () => window.clearInterval(interval)
  }, [hasBoundary])

  if (exchange === undefined || exchange === Exchange.EXCHANGE_UNSPECIFIED) return null
  // While the status for a (possibly just-switched) venue is loading, keep the
  // whole Tooltip subtree MOUNTED and just hide it. Returning null here made
  // every venue/account switch unmount + remount the Radix Tooltip trigger,
  // and each of those cycles runs Radix's composed-ref attach/detach (which
  // sets internal Popper state) — the exact machinery a production crash
  // (React #185, nested-update limit) was thrown from. `invisible` also drops
  // the element from hit-testing, so no tooltip can open from a hidden badge.
  const pending = !status && !isError

  const alwaysOpen = status ? isAlwaysOpen(status) : false
  const isOpen = status?.status === ExchangeStatusType.EXCHANGE_STATUS_TYPE_OPEN
  const isClosed = status?.status === ExchangeStatusType.EXCHANGE_STATUS_TYPE_CLOSED

  const label = isError
    ? 'Status unavailable'
    : alwaysOpen
      ? '24/7 · Open'
      : isOpen
        ? 'Open'
        : isClosed
          ? 'Closed'
          : 'Unknown'

  const boundaryMs = status?.nextChangeMs !== undefined ? Number(status.nextChangeMs) : undefined
  const countdown =
    boundaryMs !== undefined && boundaryMs > now
      ? `${isOpen ? 'Closes' : 'Opens'} in ${formatDuration(boundaryMs - now)}`
      : undefined

  const tone = isError || (!isOpen && !isClosed) ? 'unknown' : isOpen ? 'open' : 'closed'

  const StatusIcon =
    tone === 'open' ? CheckCircle2Icon : tone === 'closed' ? XCircleIcon : CircleHelpIcon

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          aria-label={`${exchangeDisplayName(exchange)} market status: ${label}`}
          aria-hidden={pending || undefined}
          className={cn(
            'inline-flex size-4 cursor-help items-center justify-center rounded-full border',
            tone === 'unknown' && 'border-white/15 bg-white/10 text-white/70',
            tone === 'open' && 'border-foreground/20 bg-foreground/10 text-chart-mint',
            tone === 'closed' && 'border-chart-red/40 bg-chart-red/15 text-chart-red',
            pending && 'invisible',
            className
          )}
        >
          <StatusIcon className="size-3.5" aria-hidden="true" />
          <span className="sr-only">{label}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent
        side="bottom"
        align="start"
        className={cn('flex flex-col gap-2', tooltipContentClassName)}
      >
        <div className="flex flex-col gap-0.5">
          <span className="text-xs font-medium">{exchangeDisplayName(exchange)}</span>
          <span className="text-xs text-white/60">
            {isError ? 'Status unavailable - retrying shortly.' : label}
          </span>
          {countdown && <span className="text-xs text-white/60">{countdown}</span>}
        </div>
        {schedule && <RegularHours schedule={schedule} />}
      </TooltipContent>
    </Tooltip>
  )
})
